
import { createClient } from '@supabase/supabase-js';
import ErrorManagementService from '@/services/error-management';

const supabaseUrl = 'https://guwcqpytunuquoirejsc.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imd1d2NxcHl0dW51cXVvaXJlanNjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzI1OTg0MTEsImV4cCI6MjA0ODE3NDQxMX0.7fTJefzPnOoJ6x4osIXrepaFAi57T8A870lzb-Eyr_Y';

// Add logging to help debug
console.log('Initializing Supabase client');

// Create consistent client options for all devices
const clientOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // Required for password reset and magic links
    storage: window.localStorage,
  },
  global: {
    headers: {
      'x-client-info': 'lovable-web',
    },
  },
};

// Create the Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, clientOptions);

// Get session using getSession() to avoid stale session issues
export const getSessionSafely = async () => {
  try {
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise<{session: null, error: Error}>((resolve) => {
      setTimeout(() => {
        resolve({
          session: null,
          error: new Error('Session fetch timeout')
        });
      }, 15000); // 15 seconds
    });
    
    // Race between session fetch and timeout
    const result = await Promise.race([
      supabase.auth.getSession().then(({ data, error }) => {
        return { session: data.session, error };
      }),
      timeoutPromise
    ]);

    // Log the session result
    if (result.session) {
      console.log('Session found:', { 
        userId: result.session.user.id,
        expiresAt: result.session.expires_at
      });
    } else {
      console.log('No active session found');
    }

    return result;
  } catch (err) {
    console.error('Unexpected error getting session:', err);
    return { session: null, error: err as Error };
  }
};

// Export a function to check the profiles table schema
export const checkProfilesSchema = async () => {
  console.log('Checking profiles table schema...');
  
  try {
    // First check the profiles table definition - Use a simple query without dot notation
    const { data: tableInfo, error: tableError } = await supabase
      .from('profiles')
      .select('*')
      .limit(1);
    
    if (tableError) {
      console.error('Error checking profiles table:', tableError);
      return { error: 'Failed to query profiles table' };
    }
    
    // Then try to get the table constraints
    const { data: constraints, error: constraintsError } = await supabase
      .rpc('get_table_constraints', { table_name: 'profiles' })
      .maybeSingle();
    
    if (constraintsError) {
      console.log('Note: Could not fetch constraints, might not have RPC function');
      return { 
        tableInfo: tableInfo, 
        message: 'Could not fetch constraints, but table exists'
      };
    }
    
    return { 
      tableInfo: tableInfo, 
      constraints: constraints,
      message: 'Successfully checked profiles table schema'
    };
  } catch (error) {
    console.error('Unexpected error checking schema:', error);
    return { error: 'Unexpected error checking schema' };
  }
};

// Export a unified session refresh method
export const refreshSupabaseSession = async () => {
  try {
    console.log('Refreshing session...', new Date().toISOString());
    
    // Use getSession consistently for session management
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Session refresh error:', error);
      throw error;
    }
    
    if (data.session) {
      console.log('Session refreshed successfully', {
        userId: data.session.user.id,
        expiresAt: data.session.expires_at,
      });
      return data.session;
    } else {
      console.log('No valid session found during refresh');
      return null;
    }
  } catch (error) {
    console.error('Error refreshing session:', error);
    throw error;
  }
};
